import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { FormControl, InputGroup, ListGroup, Spinner } from 'react-bootstrap';
import { debounce } from 'lodash';
import UtilityService from '../../services/UtillityServices/UtilityService';
import { v4 as uuidv4 } from "uuid";

const CustomSearch = ({ 
  setSelectedItem, 
  selectedItem, 
  searchApi,
  field,
  placeholder,
  label,
  showDate = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const searchContainerRef = useRef(null);
  const isSelecting = useRef(false);
  const ignoreNextSearch = useRef(false);

  const performSearch = async (term) => {
    const params = {
      filtri: { [field]: term },
      limite: 10,
      pagina: 1,
      ordinamento: `${field} asc`
    };
    const result = await searchApi(params);
    return result.data;
  };

  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (term?.length >= 2 && !isSelecting.current && !ignoreNextSearch.current) {
        setIsLoading(true);
        setNoResults(false);
        try {
          const result = await performSearch(term);
          setSearchResults(result);
          setNoResults(result?.length === 0);
        } catch (error) {
          console.error('Errore nella ricerca:', error);
          setSearchResults([]);
          setNoResults(true);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSearchResults([]);
        setNoResults(false);
      }
    }, 300),
    [field]
  );

  useEffect(() => {
    if (searchTerm?.length >= 2 && !isSelecting.current && !ignoreNextSearch.current) {
      debouncedSearch(searchTerm);
    } else {
      setSearchResults([]);
      setNoResults(false);
    }
    return () => debouncedSearch.cancel();
  }, [searchTerm]);

  useEffect(() => {
    if (selectedItem) {
      isSelecting.current = true;
      setSearchTerm(''); 
      setSearchResults([]);
      setIsLoading(false);
      setTimeout(() => {
        isSelecting.current = false;
      }, 0);
    }
  }, [selectedItem, field]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectItem = (item) => {
    if (item) {
      isSelecting.current = true;
      setSelectedItem(item);
      setSearchTerm('');
      setSearchResults([]);
      setIsLoading(false);
      ignoreNextSearch.current = true; 
      setTimeout(() => {
        isSelecting.current = false;
        ignoreNextSearch.current = false;
      }, 0);
    }
  };

  const handleChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSelectedItem(null);
    setIsLoading(false);
    setNoResults(false);
  };

  return (
    <div className="search-container" ref={searchContainerRef}>
      <span className='small text-muted d-block mb-1'><b>{label}</b></span>
      <InputGroup className="mb-3">
        <FormControl
          className="search-input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleChange}
          aria-describedby="clear-icon"
        />
        {searchTerm && ( 
          <InputGroup.Text id="clear-icon" onClick={handleClearSearch} role='button'>
            <i className="bi bi-x-lg"></i>
          </InputGroup.Text>
        )}
      </InputGroup>
      {isLoading && !isSelecting.current && (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </Spinner>
        </div>
      )}
      {!isLoading && searchResults?.length > 0 && !isSelecting.current && (
        <ListGroup className="search-results">
          {searchResults.map(item => (
            <ListGroup.Item
              role='button'
              key={uuidv4()}
              onClick={() => handleSelectItem(item)}
              className="result-item"
            >
              <span className="result-name">{item[field]} {showDate && (item.data_inizio ? `(${UtilityService.formatDate(item.data_inizio)}` : "")}{showDate && (item.data_fine ? ` - ${UtilityService.formatDate(item.data_fine)})` : "")}</span>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
      {!isLoading && noResults && searchTerm?.length >= 2 && !isSelecting.current && (
        <div className="text-center text-muted">
          Nessun risultato trovato
        </div>
      )}
    </div>
  );
}

export default CustomSearch;
